import * as tslib_1 from "tslib";
import { BehaviorSubject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { QueryParams } from '@ml/shared/types/query-params';
var CrudApiService = /** @class */ (function () {
    function CrudApiService(_http, cache) {
        if (cache === void 0) { cache = false; }
        this._http = _http;
        this.cache = cache;
        this.values = new BehaviorSubject([]);
        this.value = new BehaviorSubject(null);
        this.queryParams = new BehaviorSubject(this.getInitialQueryParams());
        this.loading = new BehaviorSubject(null);
        this.count = new BehaviorSubject(null);
        this.hasNext = new BehaviorSubject(null);
        this.hasPrevious = new BehaviorSubject(null);
        this.loadMore = false;
        this.cursor = new BehaviorSubject(null);
        this.previousCursor = new BehaviorSubject(null);
    }
    CrudApiService.prototype.refresh = function () {
        this.values = new BehaviorSubject(null);
        this.queryParams = new BehaviorSubject(this.getInitialQueryParams());
        this.loading = new BehaviorSubject(null);
        this.count = new BehaviorSubject(null);
    };
    CrudApiService.prototype.search = function (search) {
        var queryParams = tslib_1.__assign({}, this.queryParams.value);
        if (search) {
            queryParams.search = search;
        }
        else {
            delete queryParams.search;
        }
        this.queryParams.next(queryParams);
    };
    CrudApiService.prototype.pageChange = function (pageEvent) {
        var currentPage = this.queryParamsValue.page;
        if (currentPage > pageEvent.page) {
            this.loadPreviousPage();
        }
        else if (currentPage < pageEvent.page) {
            this.loadNextPage();
        }
        else {
            // changed only the size
            var queryParams = tslib_1.__assign({}, this.queryParams.value, { pageSize: pageEvent.pageSize });
            this.queryParams.next(queryParams);
        }
    };
    CrudApiService.prototype.loadNextPage = function () {
        if (!this.hasNext.value) {
            return;
        }
        var queryParams = tslib_1.__assign({}, this.queryParamsValue);
        if (this.cursor.value) {
            queryParams.cursor = this.cursor.value;
            delete queryParams.page;
        }
        else {
            delete queryParams.cursor;
            queryParams.page = this.queryParamsValue.page + 1;
        }
        this.loadMore = true;
        this.queryParams.next(queryParams);
    };
    CrudApiService.prototype.loadPreviousPage = function () {
        if (!this.hasPrevious.value) {
            return;
        }
        var queryParams = tslib_1.__assign({}, this.queryParamsValue);
        if (this.previousCursor.value) {
            queryParams.previousCursor = this.previousCursor.value;
            delete queryParams.page;
        }
        else {
            delete queryParams.previousCursor;
            queryParams.page = this.queryParamsValue.page - 1;
        }
        this.loadMore = true;
        this.queryParams.next(queryParams);
    };
    CrudApiService.prototype.changeParams = function (queryParams) {
        this.queryParams.next(tslib_1.__assign({}, queryParams));
    };
    CrudApiService.prototype.subscribeValues$ = function () {
        var _this = this;
        return this.queryParams.pipe(filter(function (value) { return !!value && _this.filterSubscribe(); }), tap(function () { return _this.loading.next(true); }), switchMap(function (params) { return _this.list(params); }), tap(function (response) { return _this.count.next(response.count); }), tap(function (response) {
            _this.hasNext.next(!!response.next);
            _this.hasPrevious.next(!!response.previous);
        }), tap(function (response) { return _this.setCursor(response); }), map(function (response) { return response.results; }), switchMap(function (results) {
            _this.setValues(results);
            _this.loadMore = false;
            return _this.values.asObservable();
        }), tap(function () { return _this.loading.next(false); }));
    };
    CrudApiService.prototype.subscribeValue$ = function (valueId) {
        var _this = this;
        return this.get(valueId).pipe(switchMap(function (value) {
            _this.value.next(value);
            return _this.value.asObservable();
        }));
    };
    CrudApiService.prototype.list = function (queryParams) {
        var params = this.getParams(queryParams);
        return this._http.get("" + this.url, { params: params });
    };
    CrudApiService.prototype.get = function (valueId) {
        return this._http.get(this.url + "/" + valueId);
    };
    CrudApiService.prototype.add = function (value) {
        var _this = this;
        return this._http
            .post("" + this.url, value)
            .pipe(tap(function (result) { return _this.addValue(result); }));
    };
    CrudApiService.prototype.edit = function (value) {
        var _this = this;
        return this._http
            .patch(this.url + "/" + value.id, value)
            .pipe(tap(function (result) { return _this.editValue(result); }));
    };
    CrudApiService.prototype.delete = function (id) {
        var _this = this;
        return this._http.delete(this.deleteUrl(id)).pipe(tap(function () { return _this.deleteValue(id); }), tap(function () { return _this.getNextPageAfterDelete(); }));
    };
    Object.defineProperty(CrudApiService.prototype, "loading$", {
        get: function () {
            return this.loading.asObservable();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CrudApiService.prototype, "count$", {
        get: function () {
            return this.count.asObservable();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CrudApiService.prototype, "hasNext$", {
        get: function () {
            return this.hasNext.asObservable();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CrudApiService.prototype, "queryParams$", {
        get: function () {
            return this.queryParams.asObservable();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CrudApiService.prototype, "queryParamsValue", {
        get: function () {
            return this.queryParams.getValue();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CrudApiService.prototype, "currentValue", {
        get: function () {
            return this.value.value;
        },
        enumerable: true,
        configurable: true
    });
    CrudApiService.prototype.deleteUrl = function (id) {
        return this.url + "/" + id;
    };
    /**
     * Method used for subclasses to stop the observable chain
     * For example if the route is a nested one and we need to wait for a
     * specific resource
     */
    CrudApiService.prototype.filterSubscribe = function () {
        return true;
    };
    CrudApiService.prototype.setValues = function (values) {
        if (!(this.cache && this.loadMore)) {
            this.values.next(values);
        }
        else {
            this.values.next(this.values.value.concat(values));
        }
    };
    CrudApiService.prototype.addValue = function (value) {
        if (!this.useSubscribe) {
            return;
        }
        this.values.next([value].concat(this.values.value));
    };
    CrudApiService.prototype.editValue = function (value) {
        if (this.value.value && value.id === this.value.value.id) {
            return this.value.next(value);
        }
        if (!this.useSubscribe) {
            return;
        }
        var index = this.values.value.findIndex(function (item) { return item.id === value.id; });
        this.values.value[index] = value;
        this.values.next(this.values.value.slice());
    };
    /**
     * Get next page after delete
     * This method is used only if the cursor pagination is used
     */
    CrudApiService.prototype.getNextPageAfterDelete = function () {
        if (!this.useSubscribe || !this.hasNext.value || !this.cursor.value) {
            return;
        }
        if (this.values.value.length < this.queryParamsValue.pageSize) {
            this.loadNextPage();
        }
    };
    CrudApiService.prototype.deleteValue = function (valueId) {
        if (!this.useSubscribe) {
            return;
        }
        var index = this.values.value.findIndex(function (item) { return item.id === valueId; });
        this.values.value.splice(index, 1);
        this.values.next(this.values.value.slice());
    };
    CrudApiService.prototype.getParams = function (queryParams) {
        var pageSize = queryParams ? queryParams.pageSize.toString() : '10';
        var params = tslib_1.__assign({}, queryParams, { pageSize: pageSize });
        if (queryParams.page) {
            params.page = queryParams.page.toString();
        }
        else {
            delete params.page;
        }
        if (queryParams.cursor) {
            params.cursor = queryParams.cursor;
        }
        return params;
    };
    CrudApiService.prototype.getInitialQueryParams = function () {
        return new QueryParams();
    };
    Object.defineProperty(CrudApiService.prototype, "useSubscribe", {
        get: function () {
            return !!this.values.value;
        },
        enumerable: true,
        configurable: true
    });
    CrudApiService.prototype.setCursor = function (response) {
        var cursor = this.getCursorFromURL(response.next);
        var previousCursor = this.getCursorFromURL(response.previous);
        this.cursor.next(cursor);
        this.previousCursor.next(previousCursor);
    };
    CrudApiService.prototype.getCursorFromURL = function (url) {
        var name = 'cursor';
        var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        var results = regex.exec(url);
        return results === null
            ? null
            : decodeURIComponent(results[1].replace(/\+/g, ' '));
    };
    return CrudApiService;
}());
export { CrudApiService };
